import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { useLanguage } from "../../contexts/LanguageContext";

const VALUE_ICONS = ["01", "02", "03", "04"];

export const VisionSection = () => {
  const { direction, translations: t } = useLanguage();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  const v = t.vision;

  return (
    <section id="vision" className="py-20 md:py-28 bg-gradient-to-b from-[#1A1008] to-[#0D0A07] relative overflow-hidden scroll-mt-20" data-testid="vision-section" ref={ref}>
      {/* Subtle gold grain overlay */}
      <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23n)\'/%3E%3C/svg%3E")' }} />
      
      <div className="max-w-5xl mx-auto px-4 md:px-8 relative z-10">
        {/* Brand name */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <span className="text-[#C9A96E] text-xs tracking-[0.4em] uppercase block mb-4">{v.label}</span>
          <h2 className="font-['Heebo'] text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-3">
            {v.brandName}
          </h2>
          <div className="w-24 h-[2px] bg-gradient-to-r from-transparent via-[#C9A96E] to-transparent mx-auto" />
        </motion.div>

        {/* Vision Statement */}
        <motion.div
          className="mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.2, duration: 0.8 }}
        >
          <div className="relative max-w-3xl mx-auto text-center">
            <div className="absolute -top-6 left-1/2 -translate-x-1/2 text-[#C9A96E]/10 text-8xl font-serif leading-none select-none">"</div>
            <p className="text-lg md:text-xl text-white/90 leading-relaxed font-light italic relative z-10">
              {v.statement.text}
            </p>
          </div>
        </motion.div>

        {/* Mission */}
        <motion.div
          className="mb-16 max-w-3xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.35, duration: 0.8 }}
        >
          <div className="border border-[#C9A96E]/20 rounded-2xl p-8 md:p-10 bg-white/[0.02] backdrop-blur-sm">
            <h3 className="font-['Heebo'] text-lg font-bold text-[#C9A96E] mb-4 tracking-wide">
              {v.mission.title}
            </h3>
            <p className="text-white/75 leading-relaxed">
              {v.mission.text}
            </p>
          </div>
        </motion.div>

        {/* Values */}
        <motion.div
          className="mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.5, duration: 0.8 }}
        >
          <h3 className="font-['Heebo'] text-lg font-bold text-[#C9A96E] mb-8 text-center tracking-wide">
            {v.values.title}
          </h3>
          <div className="grid md:grid-cols-2 gap-5">
            {v.values.items.map((val, i) => (
              <motion.div
                key={i}
                className="group relative bg-white/[0.03] border border-[#C9A96E]/10 rounded-xl p-6 hover:border-[#C9A96E]/30 hover:bg-[#C9A96E]/[0.04] transition-all duration-300"
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.5 + i * 0.1, duration: 0.6 }}
                data-testid={`vision-value-${i}`}
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#C9A96E]/10 flex items-center justify-center shrink-0 group-hover:bg-[#C9A96E]/20 transition-colors">
                    <span className="text-[#C9A96E] text-sm font-bold">{VALUE_ICONS[i]}</span>
                  </div>
                  <div>
                    <h4 className="font-['Heebo'] text-base font-bold text-white mb-2">{val.title}</h4>
                    <p className="text-white/60 text-sm leading-relaxed">{val.text}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Promise */}
        <motion.div
          className="text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.8, duration: 0.8 }}
        >
          <div className="max-w-2xl mx-auto border-t border-[#C9A96E]/20 pt-10">
            <h3 className="font-['Heebo'] text-lg font-bold text-[#C9A96E] mb-4 tracking-wide">
              {v.promise.title}
            </h3>
            <p className="text-white text-xl md:text-2xl font-bold mb-4 font-['Heebo']">
              "{v.promise.quote}"
            </p>
            <p className="text-white/70 leading-relaxed">
              {v.promise.text}
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
